import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, unref as _unref, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, createCommentVNode as _createCommentVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, isRef as _isRef, vShow as _vShow, withDirectives as _withDirectives } from "vue"

const _hoisted_1 = /*#__PURE__*/_createElementVNode("option", {
  value: "",
  disabled: "",
  hidden: ""
}, "Choose a Metric", -1)
const _hoisted_2 = ["value"]
const _hoisted_3 = /*#__PURE__*/_createElementVNode("option", {
  value: "",
  disabled: "",
  hidden: ""
}, "Choose a Filter", -1)
const _hoisted_4 = ["value"]
const _hoisted_5 = { class: "control" }
const _hoisted_6 = ["disabled"]
const _hoisted_7 = /*#__PURE__*/_createElementVNode("span", null, "Apply", -1)
const _hoisted_8 = { class: "control" }
const _hoisted_9 = { class: "content is-small" }
const _hoisted_10 = {
  key: 0,
  class: "has-text-danger"
}


const __default__ = {
  name: "ReportFilter",
};
import { useField, useForm } from "vee-validate";
import { map, assign, filter, find, findLast, get } from "lodash";
import {
  IReportExisting,
  IReportField,
  IReportFilter,
  IReportValue,
} from "./Typings/ReportTypes";
import { computed, toRef, watch } from "vue";
import { UseValidateHelper } from "../../Helper/Functional/UseValidateHelper";


export default /*#__PURE__*/_defineComponent({
  ...__default__,
  props: {
    existing: null,
    fields: null,
    filters: null,
    value: null
  },
  emits: ["input", "delete", "emittedValidations"],
  setup(__props: any, { emit }: { emit: ({
  (e: "input", val: IReportValue): void;
  (e: "delete", val: number): void;
  (e: "emittedValidations", val: boolean): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
  existing: IReportExisting[];
  fields: IReportField[];
  filters: IReportFilter[];
  value: IReportValue;
}





//Computed values
const availableFields = computed(() => {
  let rtn = props.fields;

  return map(rtn, function (value) {
    assign(value, {
      allowed_filters: getAllowedFiltersForField(value.name),
    });

    return value;
  });
});

const availableFilters = computed(() => {
  if (props.value.metric === "") {
    return props.filters;
  } else {
    return getAllowedFiltersForField(props.value.metric);
  }
});
//Helper methods
const getAllowedFiltersForField = (field: string) => {
  return filter(props.filters, function (value) {
    let existingRule = find(props.existing, {
      configuration: { metric: field },
    });

    // No rules exist for this metric
    if (existingRule === undefined) {
      return true;
    }

    let lastRule = findLast(props.existing, {
      configuration: { metric: field },
    });

    // There can only ever be a maximum of 2 rules per metric (for a between)
    if (existingRule.configuration.rule !== lastRule?.configuration.rule) {
      return false;
    }

    // If the existing rule is 'equal to', then its the only rule that can exist for that particular metric
    if (get(existingRule, "configuration.filter", false) === "=") {
      return false;
    }

    // If the existing rule is anything but 'equal to', equal to shouldn't show up
    if (get(existingRule, "configuration.filter", false) !== "=" && value.type === "=") {
      return false;
    }

    // If the existing rule
    return (
      (value.bitwise |
        get(
          find(props.filters, {
            type: existingRule.configuration.filter,
          }),
          "bitwise",
          0
        )) !==
      value.bitwise
    );
  });
};

const valid = computed(() => {
  return (
    props.value.metric !== "" &&
    props.value.filter !== "" &&
    props.value.value !== "" &&
    !errorMessage.value
  );
});

//Validation methods
const inputValidation = () => {
  // If the metric hasn't been selected yet, you can't work out a min or max value
  if (props.value.metric !== undefined) {
    let existingRule = find(props.existing, {
      configuration: { metric: props.value.metric },
    });

    // No rules exist for this metric
    if (existingRule === undefined) {
      return "The value field must be numeric and may contain decimal points.";
    }

    if (
      (4 |
        get(
          find(props.filters, { type: existingRule.configuration.filter }),
          "bitwise",
          0
        )) !==
      4
    ) {
      return `The value field must be numeric, may contain decimal points and a minimal value of ${
        parseInt(existingRule.configuration.value) + 0.01
      }`;
    }
    if (
      (8 |
        get(
          find(props.filters, { type: existingRule.configuration.filter }),
          "bitwise",
          0
        )) !==
      8
    ) {
      return `The value field must be numeric, may contain decimal points and a maximum value of ${
        parseInt(existingRule.configuration.value) - 0.01
      }`;
    }
  }
};
const { isRequired, numericalAndDecimalValidator } = UseValidateHelper();
const {
  errorMessage: filterErrorMessage,
  value: filterValue,
  validate: filterValidate,
} = useField<IReportValue["filter"]>(
  "filterValidation",
  isRequired("The metric is required")
);
const { errorMessage, value: inputValue, validate: inputValidate } = useField<
  IReportValue["value"]
>("inputValidation", numericalAndDecimalValidator(inputValidation() as string));
const { handleSubmit } = useForm();
const save = handleSubmit(async () => {
  return Promise.all([inputValidate, filterValidate])
    .then(async (res) => {
      const result = await Promise.all(res.map((f) => f()));
      const indicator = result.every((v) => v.valid);
      toRef(props.value, "editing").value = !indicator;
      emit("emittedValidations", indicator);
      if (indicator) {
        emit("input", props.value);
      }
    })
    .catch((err) => {
      throw new Error(err);
    });
});

//Create watchers for v-model values
watch(
  () => props.value.metric,
  () => {
    if (find(availableFilters.value, { type: props.value.filter }) === undefined) {
      let rtn = props.value;

      assign(rtn, { filter: "" });

      emit("input", rtn);
    }
  }
);

watch(inputValue, (newVal: IReportValue["value"]) => {
  toRef(props.value, "value").value = newVal;
});
watch(filterValue, (newVal: IReportValue["value"]) => {
  toRef(props.value, "filter").value = newVal;
});

return (_ctx: any,_cache: any) => {
  const _component_o_select = _resolveComponent("o-select")!
  const _component_o_field = _resolveComponent("o-field")!
  const _component_o_input = _resolveComponent("o-input")!
  const _component_o_icon = _resolveComponent("o-icon")!

  return (_openBlock(), _createElementBlock("div", null, [
    _createVNode(_component_o_field, {
      grouped: "",
      "group-multiline": ""
    }, {
      default: _withCtx(() => [
        _createVNode(_component_o_field, null, {
          default: _withCtx(() => [
            _createVNode(_component_o_select, {
              placeholder: "Metric",
              modelValue: __props.value.metric,
              "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((__props.value.metric) = $event)),
              disabled: !__props.value.editing
            }, {
              default: _withCtx(() => [
                _hoisted_1,
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(availableFields), (field) => {
                  return (_openBlock(), _createElementBlock(_Fragment, null, [
                    (field.allowed_filters.length > 0)
                      ? (_openBlock(), _createElementBlock("option", {
                          value: field.name,
                          key: field.name
                        }, _toDisplayString(field.display), 9, _hoisted_2))
                      : _createCommentVNode("", true)
                  ], 64))
                }), 256))
              ]),
              _: 1
            }, 8, ["modelValue", "disabled"])
          ]),
          _: 1
        }),
        _createVNode(_component_o_field, null, {
          default: _withCtx(() => [
            _createVNode(_component_o_select, {
              modelValue: _unref(filterValue),
              "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => (_isRef(filterValue) ? (filterValue).value = $event : null)),
              disabled: !__props.value.editing,
              name: "filter"
            }, {
              default: _withCtx(() => [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(availableFilters), (filter) => {
                  return (_openBlock(), _createElementBlock(_Fragment, null, [
                    _hoisted_3,
                    _createElementVNode("option", {
                      value: filter.type
                    }, _toDisplayString(filter.display), 9, _hoisted_4)
                  ], 64))
                }), 256))
              ]),
              _: 1
            }, 8, ["modelValue", "disabled"])
          ]),
          _: 1
        }),
        _createVNode(_component_o_field, {
          type: { 'is-danger': _unref(filterErrorMessage) && _unref(filterErrorMessage).length }
        }, {
          default: _withCtx(() => [
            _createVNode(_component_o_input, {
              "data-vv-as": "value",
              modelValue: _unref(inputValue),
              "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => (_isRef(inputValue) ? (inputValue).value = $event : null)),
              step: "0.01",
              disabled: !__props.value.editing,
              name: "valueInput"
            }, null, 8, ["modelValue", "disabled"])
          ]),
          _: 1
        }, 8, ["type"]),
        _createVNode(_component_o_field, null, {
          default: _withCtx(() => [
            _createElementVNode("p", _hoisted_5, [
              _withDirectives(_createElementVNode("button", {
                class: "button is-success",
                onClick: _cache[3] || (_cache[3] = 
//@ts-ignore
(...args) => (_unref(save) && _unref(save)(...args))),
                disabled: !_unref(valid)
              }, [
                _hoisted_7,
                _createVNode(_component_o_icon, { icon: "check" })
              ], 8, _hoisted_6), [
                [_vShow, __props.value.editing]
              ])
            ]),
            _createElementVNode("p", _hoisted_8, [
              _createElementVNode("button", {
                class: "button is-danger",
                onClick: _cache[4] || (_cache[4] = ($event: any) => (_ctx.$emit('delete', __props.value.rule)))
              }, [
                _createVNode(_component_o_icon, { icon: "close" })
              ])
            ])
          ]),
          _: 1
        })
      ]),
      _: 1
    }),
    _createElementVNode("div", _hoisted_9, [
      (_unref(errorMessage))
        ? (_openBlock(), _createElementBlock("p", _hoisted_10, _toDisplayString(_unref(errorMessage)), 1))
        : _createCommentVNode("", true)
    ])
  ]))
}
}

})